import { useId, useState, type ReactNode } from 'react';
import { formatOptionPrice } from './formatOptionPrice';
import type { CatalogOption } from '../types';

/**
 * 요약 두 번째 줄은 가격이거나 설명 문구입니다.
 *
 * 둘을 한 타입으로 두지 않고 갈라 놓은 이유는, 값을 매기지 않는 선택(차량 모델,
 * 배경)에 설명을 안 넘기면 가격 자리에 표시할 것이 없어지기 때문입니다. 예전에는
 * 그게 런타임에 `+₩NaN`으로 드러났습니다. 이제는 컴파일 시점에 걸립니다.
 */
export type OptionSectionProps = {
  title: string;
  children?: ReactNode;
  /** false면 제목은 버튼이 아니며 본문이 항상 보입니다. */
  collapsible?: boolean;
  /** 접을 수 있는 섹션의 최초 상태. 우측바를 짧게 유지하려고 기본값은 false입니다. */
  defaultExpanded?: boolean;
} & (
  | {
      /** 가격을 요약 줄에 그대로 보여 주는 옵션(색상·휠·내장). */
      selectedOption: CatalogOption;
      selectionNote?: undefined;
    }
  | {
      /** 값을 매기지 않는 선택(차량 모델·배경). 설명 문구를 대신 보여 줍니다. */
      selectedOption: { name: string };
      selectionNote: string;
    }
);

/**
 * 옵션 묶음 하나. 접힌 상태에도 제목과 현재 선택을 남기고, 본문 컨트롤만 숨깁니다.
 *
 * CSS 높이만 0으로 만들지 않고 hidden을 쓰는 이유는 화면에 없는 버튼이 키보드 Tab
 * 순서에 남는 것을 막기 위해서입니다. heading 안의 native button은 Enter/Space 동작과
 * aria-expanded 상태를 브라우저가 일관되게 제공합니다.
 */
export function OptionSection(props: OptionSectionProps) {
  const {
    title,
    selectedOption,
    children,
    collapsible = true,
    defaultExpanded = false,
  } = props;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const headingId = useId();
  const bodyId = useId();
  const contentVisible = !collapsible || expanded;
  const note =
    props.selectionNote === undefined
      ? formatOptionPrice(props.selectedOption.price)
      : props.selectionNote;

  const sectionClassName = [
    'option-section',
    collapsible ? '' : 'option-section--fixed',
    contentVisible ? '' : 'option-section--collapsed',
  ].filter(Boolean).join(' ');

  return (
    <section
      className={sectionClassName}
      aria-labelledby={headingId}
    >
      <header className="option-section__header">
        <h2 className="option-section__title" id={headingId}>
          {collapsible ? (
            <button
              type="button"
              className="option-section__toggle"
              aria-expanded={expanded}
              aria-controls={bodyId}
              onClick={() => setExpanded((current) => !current)}
            >
              <span>{title}</span>
              <span className="option-section__chevron" aria-hidden="true">›</span>
            </button>
          ) : title}
        </h2>
        <p className="option-section__selection">
          <span className="option-section__selected-name">{selectedOption.name}</span>
          <span className="option-section__selected-price">{note}</span>
        </p>
      </header>
      <div className="option-section__body" id={bodyId} hidden={!contentVisible}>
        {children}
      </div>
    </section>
  );
}
